import React, { useState, useCallback, useMemo } from 'react';
import type { DnsRecord } from '@troia-v3/types';
import { Icon } from '@/shared/icons'
import { fullRecordName, sharedSubdomainLabel } from '../utils/dnsRecords';
interface DnsRecordsTableProps {
  /** Lista de registros DNS */
  records: DnsRecord[];
  /** Nome do dominio para exibir nomes completos (ex: troiachat.com) */
  domain?: string;
}

const DNS_STATUS_COLORS: Record<string, { bg: string; text: string; label: string }> = {
  verified: { bg: 'var(--theme-success-subtle)', text: 'var(--theme-success-fg)', label: 'Verificado' },
  pending: { bg: 'var(--theme-warning-subtle)', text: 'var(--theme-warning-fg)', label: 'Pendente' },
  failed: { bg: 'var(--theme-error-subtle)', text: 'var(--theme-error-fg)', label: 'Falhou' },
  not_started: { bg: 'var(--theme-surface-sunken)', text: 'var(--theme-muted-text)', label: 'Nao Iniciado' },
};

const SECTION_CONFIG: Record<string, { label: string; description: string }> = {
  DKIM: { label: 'DKIM', description: 'Assinatura digital para autenticar seus emails' },
  SPF: { label: 'SPF', description: 'Autoriza servidores para enviar emails pelo seu dominio' },
  MX: { label: 'MX', description: 'Configura recebimento de emails no seu dominio' },
  DMARC: { label: 'DMARC', description: 'Politica de autenticacao de emails' },
  TRACKING: { label: 'Rastreamento', description: 'Necessario para confirmar a abertura e os cliques dos emails' },
  TRACKING_CAA: { label: 'Rastreamento (CAA)', description: 'Autoriza o certificado de seguranca do endereco de rastreamento' },
};

interface DnsRecordGroup {
  section: string;
  label: string;
  description: string;
  records: DnsRecord[];
}

/**
 * Tabela de registros DNS agrupados por secao (DKIM, SPF, MX, Rastreamento).
 *
 * @example
 * ```tsx
 * <DnsRecordsTable records={domain.dnsRecords} domain="troiachat.com" />
 * ```
 */
export const DnsRecordsTable: React.FC<DnsRecordsTableProps> = ({ records, domain }) => {
  const [copiedIndex, setCopiedIndex] = useState<string | null>(null);

  /** Trecho do dominio que o provedor ja repete nos nomes (dominio que e subdominio) */
  const sharedLabel = useMemo(
    () => (domain ? sharedSubdomainLabel(records ?? [], domain) : ''),
    [records, domain],
  );

  /** Exibe o nome completo do registro DNS */
  const getFullRecordName = (name: string): string => fullRecordName(name, domain, sharedLabel);

  const handleCopy = useCallback(async (value: string, key: string) => {
    try {
      await navigator.clipboard.writeText(value);
      setCopiedIndex(key);
      setTimeout(() => setCopiedIndex(null), 2000);
    } catch {
      // Fallback silencioso
    }
  }, []);

  /** Agrupa registros por secao (DKIM, SPF, MX) */
  const groups = useMemo((): DnsRecordGroup[] => {
    if (!records || records.length === 0) return [];

    const groupMap = new Map<string, DnsRecord[]>();
    const order: string[] = [];

    for (const record of records) {
      const section = record.record || 'SPF';
      if (!groupMap.has(section)) {
        groupMap.set(section, []);
        order.push(section);
      }
      groupMap.get(section)!.push(record);
    }

    return order.map((section) => {
      const config = SECTION_CONFIG[section] || { label: section, description: '' };
      return {
        section,
        label: config.label,
        description: config.description,
        records: groupMap.get(section) || [],
      };
    });
  }, [records]);

  if (!records || records.length === 0) {
    return (
      <div
        className="text-center py-8 text-sm"
        style={{ color: 'var(--theme-muted-text)' }}
      >
        Nenhum registro DNS encontrado.
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {groups.map((group) => (
        <div
          key={group.section}
          className="rounded-lg border overflow-hidden"
          style={{ borderColor: 'var(--theme-card-border)' }}
        >
          {/* Section Header */}
          <div
            className="px-4 py-2.5 flex items-center gap-2"
            style={{
              backgroundColor: 'var(--theme-page-background)',
              borderBottom: '1px solid var(--theme-card-border)',
            }}
          >
            <span
              className="inline-flex items-center px-2 py-0.5 rounded text-xs font-bold"
              style={{
                backgroundColor: 'var(--theme-primary-background)',
                color: 'var(--theme-primary)',
              }}
            >
              {group.label}
            </span>
            {group.description && (
              <span
                className="text-xs"
                style={{ color: 'var(--theme-muted-text)' }}
              >
                {group.description}
              </span>
            )}
          </div>

          {/* Records Table */}
          <table className="w-full text-sm">
            <thead>
              <tr
                style={{
                  backgroundColor: 'var(--theme-page-background)',
                  borderBottom: '1px solid var(--theme-card-border)',
                }}
              >
                <th
                  className="text-left px-4 py-2 font-medium text-xs uppercase tracking-wider"
                  style={{ color: 'var(--theme-muted-text)', width: '80px' }}
                >
                  Tipo
                </th>
                <th
                  className="text-left px-4 py-2 font-medium text-xs uppercase tracking-wider"
                  style={{ color: 'var(--theme-muted-text)', width: '200px' }}
                >
                  Nome
                </th>
                <th
                  className="text-left px-4 py-2 font-medium text-xs uppercase tracking-wider"
                  style={{ color: 'var(--theme-muted-text)' }}
                >
                  Valor
                </th>
                {group.records.some((r) => r.priority != null) && (
                  <th
                    className="text-left px-4 py-2 font-medium text-xs uppercase tracking-wider"
                    style={{ color: 'var(--theme-muted-text)', width: '90px' }}
                  >
                    Prioridade
                  </th>
                )}
                <th
                  className="text-left px-4 py-2 font-medium text-xs uppercase tracking-wider"
                  style={{ color: 'var(--theme-muted-text)', width: '110px' }}
                >
                  Status
                </th>
              </tr>
            </thead>
            <tbody>
              {group.records.map((record, idx) => {
                const statusConfig = DNS_STATUS_COLORS[record.status] || DNS_STATUS_COLORS.not_started;
                const copyKey = `${group.section}-${idx}`;
                const hasPriority = group.records.some((r) => r.priority != null);
                const fullName = getFullRecordName(record.name);

                return (
                  <tr
                    key={copyKey}
                    className={idx > 0 ? 'border-t' : ''}
                    style={idx > 0 ? { borderColor: 'var(--theme-card-border)' } : {}}
                  >
                    <td
                      className="px-4 py-3 font-mono text-xs font-medium"
                      style={{ color: 'var(--theme-card-text)' }}
                    >
                      {record.type}
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-1.5">
                        <code
                          className="font-mono text-xs truncate block max-w-[180px]"
                          style={{ color: 'var(--theme-card-text)' }}
                          title={fullName}
                        >
                          {fullName}
                        </code>
                        <button
                          type="button"
                          onClick={() => handleCopy(fullName, `name-${copyKey}`)}
                          className="p-0.5 rounded transition-colors flex-shrink-0"
                          style={{ color: 'var(--theme-muted-text)' }}
                          onMouseEnter={(e) => {
                            e.currentTarget.style.backgroundColor = 'var(--theme-button-ghost-hover)';
                          }}
                          onMouseLeave={(e) => {
                            e.currentTarget.style.backgroundColor = 'transparent';
                          }}
                          title="Copiar nome"
                        >
                          {copiedIndex === `name-${copyKey}` ? (
                            <Icon name="check" className="w-3 h-3" style={{ color: '#16a34a' }} />
                          ) : (
                            <Icon name="copy" className="w-3 h-3" />
                          )}
                        </button>
                      </div>
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-1.5">
                        <code
                          className="font-mono text-xs max-w-[320px] truncate block"
                          style={{ color: 'var(--theme-card-text)' }}
                          title={record.value}
                        >
                          {record.value}
                        </code>
                        <button
                          type="button"
                          onClick={() => handleCopy(record.value, `val-${copyKey}`)}
                          className="p-0.5 rounded transition-colors flex-shrink-0"
                          style={{ color: 'var(--theme-muted-text)' }}
                          onMouseEnter={(e) => {
                            e.currentTarget.style.backgroundColor = 'var(--theme-button-ghost-hover)';
                          }}
                          onMouseLeave={(e) => {
                            e.currentTarget.style.backgroundColor = 'transparent';
                          }}
                          title="Copiar valor"
                        >
                          {copiedIndex === `val-${copyKey}` ? (
                            <Icon name="check" className="w-3 h-3" style={{ color: '#16a34a' }} />
                          ) : (
                            <Icon name="copy" className="w-3 h-3" />
                          )}
                        </button>
                      </div>
                    </td>
                    {hasPriority && (
                      <td
                        className="px-4 py-3 font-mono text-xs"
                        style={{ color: 'var(--theme-card-text)' }}
                      >
                        {record.priority != null ? record.priority : '-'}
                      </td>
                    )}
                    <td className="px-4 py-3">
                      <span
                        className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium"
                        style={{
                          backgroundColor: statusConfig.bg,
                          color: statusConfig.text,
                        }}
                      >
                        {statusConfig.label}
                      </span>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      ))}
    </div>
  );
};

DnsRecordsTable.displayName = 'DnsRecordsTable';
